import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { FaqDialogMode } from '../program-create-faqs/AddFaqDialog';
import type { ProgramEditStep } from '../program-edit/ProgramEditShell';
import type { ProgramAction } from '../program-actions-menu';
import { useProgramExportMenu } from '../program-export/use-program-export-menu';
import { toast } from '@eureca/ui';
import { useDeleteProgram, usePatchProgram } from '../program-edit/program-mutations';
import { programByIdQueryOptions } from '../program-edit/program-by-id-query';
import { isProgramReadOnly, mapProgramStatusKey } from '../program-edit/program-status';
import {
  ProgramEditFaqsPage as ProgramEditFaqsPageComponent,
  type ProgramFaq,
} from './ProgramEditFaqsPage.component';
import { useDefaultFaqsPanel } from '@/shared/default-faqs/use-default-faqs-panel';

function createFaqId() {
  return `faq_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function sameFaqs(a: readonly ProgramFaq[], b: readonly ProgramFaq[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((faq, index) => {
    const other = b[index];
    if (!other) return false;
    return faq.id === other.id && faq.question === other.question && faq.answer === other.answer;
  });
}

export default function ProgramEditFaqsPage() {
  const navigate = useNavigate();
  const { programId } = useParams({
    from: '/programas/$programId/editar/perguntas-frequentes',
  });
  const { copy: isCopy = false } = useSearch({
    from: '/programas/$programId/editar/perguntas-frequentes',
  });

  const { data: program } = useSuspenseQuery(programByIdQueryOptions(programId));
  const deleteProgram = useDeleteProgram(programId);
  const patchProgram = usePatchProgram(programId);
  const { exportTypes, handleExportAction } = useProgramExportMenu(programId);

  // EU-570 — status REAL do programa (antes era hardcoded 'ativa');
  // Encerrado/Cancelado = read-only (Salvar alterações desabilitado na
  // última etapa; cards e "Adicionar nova pergunta" bloqueados).
  const status = isCopy ? ('rascunho' as const) : mapProgramStatusKey(program.statusKey);
  const isReadOnly = !isCopy && isProgramReadOnly(status);

  // Hydrate from `program.faqs` — Core returns the `program_faq.id` (UUID)
  // as `f.id`, stable for both seed-backed and custom rows. Use it directly
  // as the React key.
  const initialFaqs = useMemo<readonly ProgramFaq[]>(
    () =>
      program.faqs.map((f) => ({
        id: f.id,
        question: f.question,
        answer: f.answer,
      })),
    [program.faqs]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStepsOpen, setIsStepsOpen] = useState(false);
  const [faqs, setFaqs] = useState<ProgramFaq[]>(() => [...initialFaqs]);
  const isDirty = useMemo(() => !sameFaqs(faqs, initialFaqs), [faqs, initialFaqs]);
  const [isDefaultFaqsOpen, setIsDefaultFaqsOpen] = useState(false);
  // EU-641 — persistência imediata por alternância (FQC-012); busca só com o
  // painel aberto (FQC-022).
  const defaultFaqsPanel = useDefaultFaqsPanel(
    { kind: 'program', programId },
    { enabled: isDefaultFaqsOpen }
  );
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<FaqDialogMode>('add');
  const [editingFaqId, setEditingFaqId] = useState<string | null>(null);
  const [newFaqQuestion, setNewFaqQuestion] = useState('');
  const [newFaqAnswer, setNewFaqAnswer] = useState('');

  const copySearch = isCopy ? { copy: true } : {};

  function handleStepChange(step: ProgramEditStep) {
    setIsStepsOpen(false);
    if (step === 'info') {
      void navigate({
        to: '/programas/$programId/editar',
        params: { programId },
        search: copySearch,
      });
      return;
    }
    if (step === 'benefits') {
      void navigate({
        to: '/programas/$programId/editar/beneficios',
        params: { programId },
        search: copySearch,
      });
    }
  }

  function handleAddFaq() {
    setDialogMode('add');
    setEditingFaqId(null);
    setNewFaqQuestion('');
    setNewFaqAnswer('');
    setIsAddDialogOpen(true);
  }

  function handleEditFaq(id: string) {
    const faq = faqs.find((item) => item.id === id);
    if (!faq) return;
    setDialogMode('edit');
    setEditingFaqId(id);
    setNewFaqQuestion(faq.question);
    setNewFaqAnswer(faq.answer);
    setIsAddDialogOpen(true);
  }

  function handleRemoveFaq(id: string) {
    setFaqs((prev) => prev.filter((faq) => faq.id !== id));
  }

  function handleSaveNewFaq() {
    const question = newFaqQuestion.trim();
    const answer = newFaqAnswer.trim();
    if (!question || !answer) return;

    if (dialogMode === 'edit' && editingFaqId) {
      setFaqs((prev) =>
        prev.map((faq) => (faq.id === editingFaqId ? { ...faq, question, answer } : faq))
      );
    } else {
      setFaqs((prev) => [...prev, { id: createFaqId(), question, answer }]);
    }
    setIsAddDialogOpen(false);
    setEditingFaqId(null);
  }

  function handleBack() {
    void navigate({
      to: '/programas/$programId/editar/beneficios',
      params: { programId },
      search: copySearch,
    });
  }

  async function persistFaqs() {
    // Replace-set: send only `{question, answer}` per row. The local React
    // `id` is just a key — the server doesn't care about it.
    await patchProgram.mutateAsync({
      faqs: faqs.map((f) => ({ question: f.question, answer: f.answer })),
    });
  }

  async function handleSaveChanges() {
    if (isReadOnly) return; // EU-570 — o botão renderiza desabilitado.
    await persistFaqs();
    toast('Perguntas salvas', {
      action: { label: 'Fechar', onClick: () => toast.dismiss() },
    });
  }

  async function handleDiscardCopy() {
    await deleteProgram.mutateAsync();
    void navigate({ to: '/programas' });
  }

  function handleHeaderAction(action: ProgramAction) {
    // EU-654 - secao Extrair bases do header (toast de 3 estados).
    if (handleExportAction(action)) {
      return;
    }
    if (action === 'discard') {
      void handleDiscardCopy();
    }
    // Other actions (publish/end/cancel/duplicate) — wired in EU-149-followup.
  }

  async function handleSubmit() {
    if (isReadOnly) return; // EU-570 — última etapa não tem "Avançar" em visualização.
    setIsSubmitting(true);
    try {
      await persistFaqs();
      await navigate({
        to: '/programas/$programId/editar/sucesso',
        params: { programId },
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ProgramEditFaqsPageComponent
      status={status}
      readOnly={isReadOnly}
      faqs={faqs}
      onAddFaq={handleAddFaq}
      onEditFaq={handleEditFaq}
      onRemoveFaq={handleRemoveFaq}
      onViewDefaultFaqs={() => setIsDefaultFaqsOpen(true)}
      isDefaultFaqsOpen={isDefaultFaqsOpen}
      onDefaultFaqsOpenChange={setIsDefaultFaqsOpen}
      defaultFaqItems={defaultFaqsPanel.items}
      defaultFaqsLoading={defaultFaqsPanel.loading}
      defaultFaqsError={defaultFaqsPanel.error}
      onDefaultFaqsRetry={defaultFaqsPanel.refetch}
      onDefaultFaqToggle={(faqId, hidden) => {
        // EU-570 — modo visualização: switches desabilitados; guard extra.
        if (isReadOnly) return;
        defaultFaqsPanel.toggle(faqId, hidden);
      }}
      isAddDialogOpen={isAddDialogOpen}
      onAddDialogOpenChange={setIsAddDialogOpen}
      dialogMode={dialogMode}
      newFaqQuestion={newFaqQuestion}
      newFaqAnswer={newFaqAnswer}
      onNewFaqQuestionChange={setNewFaqQuestion}
      onNewFaqAnswerChange={setNewFaqAnswer}
      onSaveNewFaq={handleSaveNewFaq}
      progress={100}
      currentStep={3}
      totalSteps={3}
      isStepsOpen={isStepsOpen}
      onStepsOpenChange={setIsStepsOpen}
      onStepChange={handleStepChange}
      isSubmitting={isSubmitting || deleteProgram.isPending || patchProgram.isPending}
      isDirty={isDirty}
      isCopy={isCopy}
      onDiscardCopy={() => void handleDiscardCopy()}
      onBack={handleBack}
      onSaveChanges={() => void handleSaveChanges()}
      onSubmit={() => void handleSubmit()}
      onHeaderAction={handleHeaderAction}
      exportTypes={exportTypes}
    />
  );
}
